import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '@/assets/icons';
import { TypeToast } from '@/constant';
import { CommonActions, CommonSelectors } from '@/stores';
import { cn } from '@/utils';
import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Toast = () => {
	const dispatch = useDispatch();
	const { message, duration, type, key } = useSelector(
		CommonSelectors.selectToastMessage
	); // use key to track change

	const toastShowing = useRef<NodeJS.Timeout | null>(null);
	const [isVisible, setIsVisible] = useState(false); // use to control animation
	const [isHiding, setIsHiding] = useState(false); // use to control hide toast before show new toast

	useEffect(() => {
		if (message && key) {
			if (isVisible) {
				// if toast is showing, hide it before show new toast
				setIsHiding(true); // trigger hide current toast
				setTimeout(() => {
					setIsVisible(false); // hide current toast
					setIsHiding(false); // mark hide complete, ready to show new toast

					// show new toast after hide current toast
					setIsVisible(true);
				}, 300); // wait for hide animation complete (300ms)
			} else {
				// if no toast is showing, show new toast immediately
				setIsVisible(true);
			}

			if (toastShowing.current) {
				clearTimeout(toastShowing.current);
			}

			toastShowing.current = setTimeout(() => {
				setIsHiding(true); // start hide toast after duration
				setTimeout(() => {
					setIsVisible(false); // hide toast after hide animation complete
					setIsHiding(false);
					dispatch(
						CommonActions.showToast({
							message: '',
							duration: 5000,
							type: 'SUCCESS',
							key: new Date().getTime(), // reset key after toast hide
						})
					);
				}, 300); // wait a bit before reset key after hide animation complete
			}, duration);
		}

		return () => {
			if (toastShowing.current) {
				clearTimeout(toastShowing.current);
			}
		};
	}, [message, key, duration, dispatch]); // track key to show toast again when key changes

	if (!message) return null;

	const renderIcon = () => {
		switch (type) {
			case 'SUCCESS':
				return <SuccessIcon />;
			case 'ERROR':
				return <ErrorIcon />;
			case 'WARNING':
				return <WarningIcon />;
			case 'INFO':
				return <InfoIcon />;
			default:
				return null;
		}
	};

	return (
		<div className="center toast-container">
			<div
				className={cn(
					'absolute z-[9999] bottom-20 transition-transform duration-300',
					isHiding
						? 'translate-y-full opacity-0'
						: isVisible
							? 'translate-y-0 opacity-100'
							: 'translate-y-full opacity-0'
				)}
			>
				<div
					className={cn(
						'h-[45px] bg-white row gap-3 px-[12px] min-w-[328px] border'
					)}
					style={{
						borderColor: TypeToast[type],
					}}
				>
					{renderIcon()}
					<p className="text-[15px] font-medium text-font2">{message}</p>
				</div>
			</div>
		</div>
	);
};

export default memo(Toast);
